import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { logger as SentryLogger } from "@sentry/react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import { NavBar } from "@components/navigation/NavBar/NavBar";
import { IPricingRecord } from "@services/QuoteBuilder/QuoteBuilderService";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import ModalContainer from "@components/modal/Container";
import CircularSpinner from "@components/spinners/Circular/Circular";
import Warning from "@components/modal/info/Warning";
import { InfoBox } from "@components/cards/messageBox";
import { CustomerPotentialDetail } from "./customerPotentialDetailReducer";
import { PricingRecordAction } from "./pricingRecordReducer";
import PriceBreakdownComponent from "./PriceBreakdownComponent";
import PlanSummaryComponent from "./PlanSummaryComponent";
import { QuoteBuilderService } from "@services/QuoteBuilder/QuoteBuilderService";
import { Country } from "@utils/constants/localisation";

type Props = {
  customerPotentialDetailState: CustomerPotentialDetail;
  pricingRecordState: IPricingRecord | null;
  pricingRecordDispatch: React.Dispatch<PricingRecordAction>;
  navigateLandingPageTarget: string;
  navigateNextTarget: string;
  foQuoting21Enabled?: boolean;
};

export default function QuoteReviewView({
  customerPotentialDetailState,
  pricingRecordState,
  pricingRecordDispatch,
  navigateLandingPageTarget,
  navigateNextTarget,
  foQuoting21Enabled
}: Props) {
  const navigate = useNavigate();
  const { potentialId, country } = useParams() as {
    potentialId: string;
    country: Country;
  };
  const msalContext = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const defaultErrorMessage = `We are sorry, an error occurred while processing your request. Click to return home.`;
  const [errorVisible, setErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState(defaultErrorMessage);
  const [loading, setLoading] = useState<boolean>(false);

  // For redirecting the page back to the landing page if the pricing record is null
  useEffect(() => {
    if (pricingRecordState === null) {
      navigate(navigateLandingPageTarget);
    }
  }, [pricingRecordState, navigateLandingPageTarget, navigate]);

  /**
   * On submit handler
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If endpoint is successful, update pricing record
    try {
      // Validate pricing record before saving
      if (!pricingRecordState) {
        setErrorMessage("Missing pricing record");
        setErrorVisible(true);
        return;
      }

      setLoading(true);
      const response = await QuoteBuilderService.postPricingRecord(
        country,
        potentialId,
        {
          action: "create_and_send_quote",
          pricing_record: pricingRecordState,
        },
        { msalContext, isAuthenticated }
      );
      console.log("Successfully saved pricing record: ", response);

      // Update the pricing record in the state
      pricingRecordDispatch({
        type: "SET_PRICING_RECORD",
        payload: response.pricing_record,
      });

      // Log the pricing record
      SentryLogger.info(
        "Created and sent quote",
        {
          customerPotentialDetailState,
          pricingRecord: response.pricing_record,
        }
      );

      // If successful, redirect to Quote Status page
      navigate(navigateNextTarget);
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      setErrorVisible(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Render the component
   */
  return (
    <>
      <NavBar pageTitle="Review" />
      {loading === true && <ForegroundLoadingSpinner />}
      {errorVisible && (
        <Warning
          message={errorMessage}
          additionalInfo=""
          onClickClose={() => {
            setErrorVisible(false);
            navigate("/");
          }}
        />
      )}
      <form style={{ margin: "2.5vh 2.5vw" }} onSubmit={handleSubmit}>
        <InfoBox
          testId="InfoBox-GST-Inclusive"
          message={
            <>
              All Prices are listed as <strong>TAX Inclusive</strong> where
              applicable.
            </>
          }
        />
        <PlanSummaryComponent
          customerPotentialDetailState={customerPotentialDetailState}
          pricingRecordState={pricingRecordState}
          foQuoting21Enabled={foQuoting21Enabled}
        />
        <Divider />
        <Section
          childCmp={
            <>
              {/* Price Breakdown */}
              <PriceBreakdownComponent
                pricingRecord={pricingRecordState}
                showTotalWeeklySubscriptionPrice={true}
                showSetupFee={true}
                defaultExpanded={true}
                foQuoting21Enabled={foQuoting21Enabled}
              />
            </>
          }
        />
        {/* Submit Button */}
        {/* Disabled when there is already an existing quote, determined by the request id*/}
        <SubmitBtn
          text="Send"
          disabled={
            !pricingRecordState ||
            (pricingRecordState &&
              pricingRecordState?.zoho_sign_quote_detail?.request_id)
          }
        />
      </form>
    </>
  );
}

/**
 * Helper components
 */
const Section = ({ childCmp }: { childCmp: JSX.Element }) => (
  <div style={{ margin: "2.5vh 0vw" }}>{childCmp}</div>
);

const Divider = () => (
  <hr
    style={{ margin: "2vh 0", border: "none", borderTop: "1px solid #e0e0e0" }}
  />
);

const ForegroundLoadingSpinner = () => (
  <ModalContainer stylesCfg={{ windowTransparency: true }}>
    <LoadingSpinner />
  </ModalContainer>
);

const LoadingSpinner = () => (
  <div
    data-testid="loading-spinner"
    style={{ display: "flex", justifyContent: "center", margin: "15vh" }}
  >
    {<CircularSpinner />}
  </div>
);
